import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { apiConfig } from "../config";
const API_BASE_URL = apiConfig.baseUrl;

interface TodaysOrdersResponse {
  date: string;
  total_orders: number;
  orders: Array<{
    id: number;
    employee_name: string;
    employee_number: string | null;
    order_type: string;
    meal_name: string | null;
    order_status: "pending" | "confirmed" | "preparing" | "ready" | "completed" | "cancelled";
    toppings: string[];
    notes: string | null;
    created_at: string | null;
  }>;
}

interface MenuItem {
  id: number;
  day_of_week: number;
  menu_item: string;
  description: string | null;
  is_active: boolean;
}

interface MenuWeeksResponse {
  weeks: Record<number, MenuItem[]>;
}

interface Topping {
  id: number;
  name: string;
  category: string;
  is_available: boolean;
  sort_order: number;
}

interface ToppingsResponse {
  toppings: Topping[];
}

interface DailySpecial {
  id: number;
  date: string;
  menu_item: string;
  description: string | null;
  replaces_regular: boolean;
  replacement_mode: 'add' | 'replace_special' | 'replace_lunch';
  sort_order: number;
  meal_period?: 'lunch' | 'midnight';
}

interface DailySpecialsResponse {
  specials: DailySpecial[];
}

interface DailyReportResponse {
  start_date: string;
  end_date: string;
  data: {
    [date: string]: {
      regular: number;
      salad: number;
      special: number;
      total: number;
    };
  };
}

interface EmployeeListResponse {
  date: string;
  employees: Array<{
    employee_name: string;
    employee_number: string | null;
    order_type: string;
    meal_name: string | null;
    meal_type?: string | null;
    toppings: string[] | null;
    notes: string | null;
  }>;
}

interface WeeklyReportResponse {
  week_start: string;
  week_end: string;
  summary: {
    regular: number;
    salad: number;
    special: number;
  };
  total: number;
  meals: Array<{
    meal_name: string;
    meal_type?: string | null;
    count: number;
  }>;
}

interface MonthlyReportResponse {
  year: number;
  month: number;
  summary: {
    regular: number;
    salad: number;
    special: number;
  };
  total: number;
  daily: {
    [date: string]: number;
  };
  meals: Array<{
    meal_name: string;
    meal_type?: string | null;
    count: number;
  }>;
}

// Kitchen Orders
async function fetchTodaysOrders(date: string, mealPeriod: 'lunch' | 'midnight' = 'lunch'): Promise<TodaysOrdersResponse> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const url = new URL(`${API_BASE_URL}/admin/orders/today`, window.location.origin);
  url.searchParams.append("order_date", date);
  url.searchParams.append("meal_period", mealPeriod);

  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch orders");
  }

  return response.json();
}

async function updateOrderStatus(orderId: number, status: string): Promise<void> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const url = new URL(`${API_BASE_URL}/admin/orders/${orderId}/status`, window.location.origin);
  url.searchParams.append("status", status);

  const response = await fetch(url.toString(), {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to update order status");
  }
}

// Menu Weeks
async function fetchMenuWeeks(): Promise<MenuWeeksResponse> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const response = await fetch(`${API_BASE_URL}/admin/menu/weeks`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch menu weeks");
  }

  return response.json();
}

async function updateMenuItem(itemId: number, data: Partial<MenuItem>): Promise<void> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const response = await fetch(`${API_BASE_URL}/admin/menu/weeks/${itemId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Failed to update menu item");
  }
}

async function createMenuItem(data: { week_number: number; day_of_week: number; menu_item: string; description?: string }): Promise<void> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const response = await fetch(`${API_BASE_URL}/admin/menu/weeks`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Failed to create menu item");
  }
}

async function deleteMenuItem(itemId: number): Promise<void> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const response = await fetch(`${API_BASE_URL}/admin/menu/weeks/${itemId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to delete menu item");
  }
}

// Toppings
async function fetchToppings(): Promise<ToppingsResponse> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const response = await fetch(`${API_BASE_URL}/admin/menu/toppings`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch toppings");
  }

  return response.json();
}

async function updateTopping(toppingId: number, data: Partial<Topping>): Promise<void> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const response = await fetch(`${API_BASE_URL}/admin/menu/toppings/${toppingId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Failed to update topping");
  }
}

async function createTopping(data: { name: string; category: string; sort_order?: number }): Promise<void> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const response = await fetch(`${API_BASE_URL}/admin/menu/toppings`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Failed to create topping");
  }
}

// Daily Specials
async function fetchDailySpecials(startDate?: string, endDate?: string): Promise<DailySpecialsResponse> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const url = new URL(`${API_BASE_URL}/admin/menu/specials`, window.location.origin);
  if (startDate) url.searchParams.append("start_date", startDate);
  if (endDate) url.searchParams.append("end_date", endDate);

  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch daily specials");
  }

  return response.json();
}

async function createDailySpecial(data: { date: string; menu_item: string; description?: string; replacement_mode?: 'add' | 'replace_special' | 'replace_lunch'; hide_salad?: boolean; meal_period?: 'lunch' | 'midnight' }): Promise<void> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const response = await fetch(`${API_BASE_URL}/admin/menu/specials`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Failed to create daily special");
  }
}

async function deleteDailySpecial(specialId: number): Promise<void> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const response = await fetch(`${API_BASE_URL}/admin/menu/specials/${specialId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to delete daily special");
  }
}

// Reports
async function fetchDailyReport(startDate: string, endDate: string, mealType?: string): Promise<DailyReportResponse> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const url = new URL(`${API_BASE_URL}/admin/reports/daily-count`, window.location.origin);
  url.searchParams.append("start_date", startDate);
  url.searchParams.append("end_date", endDate);
  if (mealType && mealType !== "all") {
    url.searchParams.append("meal_type", mealType);
  }

  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch daily report");
  }

  return response.json();
}

async function fetchEmployeeList(date: string, mealType?: string): Promise<EmployeeListResponse> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const url = new URL(`${API_BASE_URL}/admin/reports/employee-list`, window.location.origin);
  url.searchParams.append("order_date", date);
  if (mealType && mealType !== "all") {
    url.searchParams.append("meal_type", mealType);
  }

  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch employee list");
  }

  return response.json();
}

async function fetchWeeklyReport(weekStart: string | null, mealType?: string): Promise<WeeklyReportResponse> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const url = new URL(`${API_BASE_URL}/admin/reports/weekly`, window.location.origin);
  if (weekStart) {
    url.searchParams.append("week_start", weekStart);
  }
  if (mealType && mealType !== "all") {
    url.searchParams.append("meal_type", mealType);
  }

  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch weekly report");
  }

  return response.json();
}

async function fetchMonthlyReport(year: number, month: number, mealType?: string): Promise<MonthlyReportResponse> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const url = new URL(`${API_BASE_URL}/admin/reports/monthly`, window.location.origin);
  url.searchParams.append("year", year.toString());
  url.searchParams.append("month", month.toString());
  if (mealType && mealType !== "all") {
    url.searchParams.append("meal_type", mealType);
  }

  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch monthly report");
  }

  return response.json();
}


// Breakfast Admin Functions
async function fetchTodaysBreakfastOrders(date: string): Promise<any> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const url = new URL(`${API_BASE_URL}/breakfast/admin/orders/today`, window.location.origin);
  url.searchParams.append('order_date', date);

  const response = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) throw new Error('Failed to fetch breakfast orders');
  return response.json();
}

async function updateBreakfastOrderStatus(orderId: number, status: string): Promise<any> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const url = new URL(`${API_BASE_URL}/breakfast/admin/orders/${orderId}/status`, window.location.origin);
  url.searchParams.append('status', status);

  const response = await fetch(url.toString(), {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) throw new Error('Failed to update breakfast order status');
  return response.json();
}

async function fetchBreakfastMenuItems(): Promise<any> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const response = await fetch(`${API_BASE_URL}/breakfast/admin/menu`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to fetch breakfast menu');
  return response.json();
}

async function createBreakfastMenuItem(data: any): Promise<any> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const response = await fetch(`${API_BASE_URL}/breakfast/admin/menu`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to create breakfast menu item');
  return response.json();
}

async function updateBreakfastMenuItem(itemId: number, data: any): Promise<any> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const response = await fetch(`${API_BASE_URL}/breakfast/admin/menu/${itemId}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to update breakfast menu item');
  return response.json();
}

async function deleteBreakfastMenuItem(itemId: number): Promise<any> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const response = await fetch(`${API_BASE_URL}/breakfast/admin/menu/${itemId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to delete breakfast menu item');
  return response.json();
}

// Hooks
function useTodaysOrders(date: string, mealPeriod: 'lunch' | 'midnight' = 'lunch', options?: { refetchInterval?: number; refetchIntervalInBackground?: boolean }) {
  return useQuery({
    queryKey: ["admin", "orders", date, mealPeriod],
    queryFn: () => fetchTodaysOrders(date, mealPeriod),
    retry: false,
    ...options,
    staleTime: 30000,
  });
}

function useUpdateOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, status }: { orderId: number; status: string }) =>
      updateOrderStatus(orderId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "orders"] });
    },
  });
}

function useMenuWeeks() {
  return useQuery({
    queryKey: ["admin", "menu", "weeks"],
    queryFn: fetchMenuWeeks,
    enabled: !!sessionStorage.getItem('hmh_access_token'),
    staleTime: 60000,
  });
}

function useUpdateMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: number; data: Partial<MenuItem> }) =>
      updateMenuItem(itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "menu", "weeks"] });
    },
  });
}

function useCreateMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { week_number: number; day_of_week: number; menu_item: string; description?: string }) =>
      createMenuItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "menu", "weeks"] });
    },
  });
}

function useDeleteMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: number) => deleteMenuItem(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "menu", "weeks"] });
    },
  });
}

function useToppings() {
  return useQuery({
    queryKey: ["admin", "menu", "toppings"],
    queryFn: fetchToppings,
    enabled: !!sessionStorage.getItem('hmh_access_token'),
    staleTime: 60000,
  });
}

function useUpdateTopping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ toppingId, data }: { toppingId: number; data: Partial<Topping> }) =>
      updateTopping(toppingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "menu", "toppings"] });
    },
  });
}

function useCreateTopping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; category: string; sort_order?: number }) =>
      createTopping(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "menu", "toppings"] });
    },
  });
}

function useDailySpecials(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ["admin", "menu", "specials", startDate, endDate],
    queryFn: () => fetchDailySpecials(startDate, endDate),
    retry: false,
    staleTime: 60000,
  });
}

function useCreateDailySpecial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { date: string; menu_item: string; description?: string; replacement_mode?: 'add' | 'replace_special' | 'replace_lunch'; hide_salad?: boolean; meal_period?: 'lunch' | 'midnight'; recurring?: boolean }) =>
      createDailySpecial(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "menu", "specials"] });
    },
  });
}

function useDeleteDailySpecial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (specialId: number) => deleteDailySpecial(specialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "menu", "specials"] });
    },
  });
}

function useDailyReport(startDate: string, endDate: string, mealType?: string) {
  return useQuery({
    queryKey: ["admin", "reports", "daily", startDate, endDate, mealType],
    queryFn: () => fetchDailyReport(startDate, endDate, mealType),
    enabled: !!startDate && !!endDate && !!sessionStorage.getItem('hmh_access_token'),
    staleTime: 60000,
  });
}

function useEmployeeList(date: string, mealType?: string) {
  return useQuery({
    queryKey: ["admin", "reports", "employee-list", date, mealType],
    queryFn: () => fetchEmployeeList(date, mealType),
    enabled: !!date && !!sessionStorage.getItem('hmh_access_token'),
    staleTime: 60000,
  });
}

function useWeeklyReport(weekStart: string | null, mealType?: string) {
  return useQuery({
    queryKey: ["admin", "reports", "weekly", weekStart, mealType],
    queryFn: () => fetchWeeklyReport(weekStart, mealType),
    enabled: !!sessionStorage.getItem('hmh_access_token'),
    staleTime: 60000,
  });
}

function useMonthlyReport(year: number, month: number, mealType?: string) {
  return useQuery({
    queryKey: ["admin", "reports", "monthly", year, month, mealType],
    queryFn: () => fetchMonthlyReport(year, month, mealType),
    enabled: !!year && !!month && !!sessionStorage.getItem('hmh_access_token'),
    staleTime: 60000,
  });
}

// Breakfast Hooks
function useTodaysBreakfastOrders(date: string, options?: any) {
  return useQuery({
    queryKey: ['admin', 'breakfast', 'orders', date],
    queryFn: () => fetchTodaysBreakfastOrders(date),
    retry: false,
    ...options,
    staleTime: 30000,
  });
}

function useUpdateBreakfastOrderStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, status }: { orderId: number; status: string }) =>
      updateBreakfastOrderStatus(orderId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'breakfast', 'orders'] });
    },
  });
}

function useBreakfastMenuItems() {
  return useQuery({
    queryKey: ['admin', 'breakfast', 'menu'],
    queryFn: fetchBreakfastMenuItems,
    retry: false,
    staleTime: 30000,
  });
}

function useCreateBreakfastMenuItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createBreakfastMenuItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'breakfast', 'menu'] });
    },
  });
}

function useUpdateBreakfastMenuItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: number; data: any }) =>
      updateBreakfastMenuItem(itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'breakfast', 'menu'] });
    },
  });
}

function useDeleteBreakfastMenuItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteBreakfastMenuItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'breakfast', 'menu'] });
    },
  });
}

export const useAdmin = {
  useTodaysOrders,
  useUpdateOrderStatus,
  useMenuWeeks,
  useUpdateMenuItem,
  useCreateMenuItem,
  useDeleteMenuItem,
  useToppings,
  useUpdateTopping,
  useCreateTopping,
  useDailySpecials,
  useCreateDailySpecial,
  useDeleteDailySpecial,
  useDailyReport,
  useEmployeeList,
  useWeeklyReport,
  useMonthlyReport,
  useTodaysBreakfastOrders,
  useUpdateBreakfastOrderStatus,
  useBreakfastMenuItems,
  useCreateBreakfastMenuItem,
  useUpdateBreakfastMenuItem,
  useDeleteBreakfastMenuItem,
  useSpecialWeeks,
  useUpdateSpecialWeekItem,
  useCreateSpecialWeekItem,
  useDeleteSpecialWeekItem,
};

// =============================================================================
// Special Weeks (5-week rotating specials)
// =============================================================================

interface SpecialWeekItem {
  id: number;
  day_of_week: number;
  menu_item: string;
  description: string | null;
  is_active: boolean;
}

interface SpecialWeeksResponse {
  weeks: Record<number, SpecialWeekItem[]>;
}

async function fetchSpecialWeeks(): Promise<SpecialWeeksResponse> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) {
    throw new Error("No authentication token found. Please log in again.");
  }
  const url = new URL(`${API_BASE_URL}/admin/menu/special-weeks`, window.location.origin);
  const response = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("Failed to fetch special weeks");
  return response.json();
}

async function updateSpecialWeekItem(itemId: number, data: { menu_item?: string; description?: string; is_active?: boolean }): Promise<void> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) throw new Error("No authentication token found");
  const url = new URL(`${API_BASE_URL}/admin/menu/special-weeks/${itemId}`, window.location.origin);
  const response = await fetch(url.toString(), {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error("Failed to update special week item");
}

async function createSpecialWeekItem(data: { week_number: number; day_of_week: number; menu_item: string; description?: string }): Promise<void> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) throw new Error("No authentication token found");
  const url = new URL(`${API_BASE_URL}/admin/menu/special-weeks`, window.location.origin);
  const response = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || "Failed to create special week item");
  }
}

async function deleteSpecialWeekItem(itemId: number): Promise<void> {
  const token = sessionStorage.getItem('hmh_access_token');
  if (!token) throw new Error("No authentication token found");
  const url = new URL(`${API_BASE_URL}/admin/menu/special-weeks/${itemId}`, window.location.origin);
  const response = await fetch(url.toString(), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("Failed to delete special week item");
}

function useSpecialWeeks() {
  return useQuery({ queryKey: ["special-weeks"], queryFn: fetchSpecialWeeks });
}

function useUpdateSpecialWeekItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: number; data: any }) => updateSpecialWeekItem(itemId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["special-weeks"] }),
  });
}

function useCreateSpecialWeekItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSpecialWeekItem,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["special-weeks"] }),
  });
}

function useDeleteSpecialWeekItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSpecialWeekItem,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["special-weeks"] }),
  });
}
